import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "formatType",
    "csvFields",
    "pdfFields",
    "excelFields",
    "fileInput",
    "analysisContainer",
    "analyzing",
    "analysisResults",
    "detectedMappings",
    "analysisError",
    "errorMessage",
    "analyzeButton",
    "analysisModal",
    "pdfTextPreview",
    "userHints",
    "modalLoading",
    "modalContent"
  ]
  static values = {
    saveThenAnalyzeText: { type: String, default: "Please save the format template first, then return to analyze the sample file." },
    appliedText: { type: String, default: "Applied!" },
    noMappingsText: { type: String, default: "No specific mappings detected. You may need to configure manually." },
    tableLabelText: { type: String, default: "Table:" },
    columnsDetectedText: { type: String, default: "Columns detected:" },
    numericFormatText: { type: String, default: "Numeric format:" },
    negativeFormatText: { type: String, default: "Negative format:" },
    bankLabelText: { type: String, default: "Bank:" },
    transactionsFoundText: { type: String, default: "Transactions Found:" },
    fieldsDetectedText: { type: String, default: "Fields Detected:" },
    columnLabels: { type: Object, default: { date: "Date Column", description: "Description Column", amount: "Amount Column", balance: "Balance Column", debit: "Debit Column", credit: "Credit Column" } },
    numericPatternLabels: { type: Object, default: { comma_decimal: "Comma decimal (1.234,56)", period_decimal: "Period decimal (1,234.56)" } },
    negativePatternLabels: { type: Object, default: { minus_prefix: "Minus prefix (-1234)", parentheses: "Parentheses ((1234))", minus_suffix: "Minus suffix (1234-)" } }
  }

  connect() {
    this.toggleFields()
    this.detectedData = null

    // Check if we should show analysis section on load
    this.checkAnalysisVisibility()
  }

  toggleFields() {
    const selectedType = this.formatTypeTarget.value

    // Hide all format-specific sections first
    this.csvFieldsTarget.classList.add("hidden")
    this.pdfFieldsTarget.classList.add("hidden")
    this.excelFieldsTarget.classList.add("hidden")

    // Show the appropriate section based on format type
    switch (selectedType) {
      case "csv":
        this.csvFieldsTarget.classList.remove("hidden")
        break
      case "excel":
        this.excelFieldsTarget.classList.remove("hidden")
        break
      case "pdf":
        this.pdfFieldsTarget.classList.remove("hidden")
        break
    }

    // Update file input accept attribute based on format type
    this.updateFileAcceptAttribute(selectedType)

    // Check if analysis section should be shown
    this.checkAnalysisVisibility()
  }

  updateFileAcceptAttribute(formatType) {
    if (!this.hasFileInputTarget) return

    const acceptMap = {
      csv: ".csv",
      excel: ".xls,.xlsx",
      pdf: ".pdf"
    }

    this.fileInputTarget.setAttribute("accept", acceptMap[formatType] || ".csv,.xls,.xlsx,.pdf")
  }

  handleFileUpload() {
    this.checkAnalysisVisibility()
  }

  checkAnalysisVisibility() {
    // Show analysis container if file is attached (on edit page)
    const hasFile = this.element.querySelector('[data-has-sample-file]')
    if (hasFile && this.hasAnalysisContainerTarget) {
      this.analysisContainerTarget.classList.remove("hidden")
    }
  }

  async analyzeSample(event) {
    event.preventDefault()

    const statementFormatId = this.element.dataset.statementFormatId
    if (!statementFormatId) {
      alert(this.saveThenAnalyzeTextValue)
      return
    }

    // Show the modal with loading state
    this.analysisModalTarget.classList.remove("hidden")
    this.modalLoadingTarget.classList.remove("hidden")
    this.modalContentTarget.classList.add("hidden")

    try {
      const response = await fetch(`/statement_formats/${statementFormatId}/analysis`, {
        headers: { "Accept": "application/json" }
      })

      const data = await response.json()

      this.modalLoadingTarget.classList.add("hidden")

      if (data.status === "success") {
        this.pdfTextPreviewTarget.textContent = data.pdf_text_preview
        this.modalContentTarget.classList.remove("hidden")
      } else {
        this.closeAnalysisModal()
        this.analysisContainerTarget.classList.remove("hidden")
        this.showAnalysisError(data.error || "Failed to load PDF preview")
      }
    } catch (error) {
      this.closeAnalysisModal()
      this.analysisContainerTarget.classList.remove("hidden")
      this.showAnalysisError(error.message || "Network error while loading preview")
    }
  }

  closeAnalysisModal() {
    this.analysisModalTarget.classList.add("hidden")
    this.userHintsTarget.value = ""
  }

  async runAnalysis(event) {
    event.preventDefault()

    const statementFormatId = this.element.dataset.statementFormatId
    const userHints = this.userHintsTarget.value.trim()

    // Close modal and show analyzing state
    this.closeAnalysisModal()
    this.analysisContainerTarget.classList.remove("hidden")
    this.analyzingTarget.classList.remove("hidden")
    this.analysisResultsTarget.classList.add("hidden")
    this.analysisErrorTarget.classList.add("hidden")

    try {
      const csrfToken = document.querySelector("[name='csrf-token']")?.content

      const body = new FormData()
      if (userHints) body.append("user_hints", userHints)

      const response = await fetch(`/statement_formats/${statementFormatId}/analysis`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: body
      })

      const data = await response.json()

      if (data.status === "success" && data.redirect_to) {
        window.location.href = data.redirect_to
      } else {
        this.analyzingTarget.classList.add("hidden")
        this.showAnalysisError(data.error || "Failed to analyze file")
      }
    } catch (error) {
      this.analyzingTarget.classList.add("hidden")
      this.showAnalysisError(error.message || "Network error while analyzing file")
    }
  }

  showAnalysisResults(data) {
    const mappings = data.mappings || {}
    const formatType = this.formatTypeTarget.value

    // Build display of detected mappings
    let mappingHtml = ""

    if (formatType === "pdf" && mappings.table_name) {
      // New structured PDF format
      mappingHtml += `<div class="flex justify-between"><span>${this.tableLabelTextValue}</span><span class="font-medium">${mappings.table_name}</span></div>`

      if (mappings.columns && mappings.columns.length > 0) {
        mappingHtml += `<div class="mt-2"><span class="font-medium">${this.columnsDetectedTextValue}</span></div>`
        mappings.columns.forEach(col => {
          const matchLabel = col.match_column === "skip" ? "⏭ skip" : `→ ${col.match_column}`
          mappingHtml += `<div class="flex justify-between pl-2"><span>${col.pdf_column_name} <span class="text-green-600">${matchLabel}</span></span><span class="text-gray-500 text-xs">${col.format}</span></div>`
        })
      }

      if (mappings.numeric_pattern) {
        const numericLabel = this.numericPatternLabelsValue[mappings.numeric_pattern] || mappings.numeric_pattern
        mappingHtml += `<div class="flex justify-between mt-2"><span>${this.numericFormatTextValue}</span><span class="font-medium">${numericLabel}</span></div>`
      }

      if (mappings.negative_pattern) {
        const negativeLabel = this.negativePatternLabelsValue[mappings.negative_pattern] || mappings.negative_pattern
        mappingHtml += `<div class="flex justify-between"><span>${this.negativeFormatTextValue}</span><span class="font-medium">${negativeLabel}</span></div>`
      }
    } else if (formatType === "pdf") {
      // Fallback for old-style PDF mappings
      if (mappings.bank_name) {
        mappingHtml += `<div class="flex justify-between"><span>${this.bankLabelTextValue}</span><span class="font-medium">${mappings.bank_name}</span></div>`
      }
      if (mappings.transaction_count) {
        mappingHtml += `<div class="flex justify-between"><span>${this.transactionsFoundTextValue}</span><span class="font-medium">${mappings.transaction_count}</span></div>`
      }
      if (mappings.detected_fields) {
        mappingHtml += `<div class="flex justify-between"><span>${this.fieldsDetectedTextValue}</span><span class="font-medium">${mappings.detected_fields.join(", ")}</span></div>`
      }
    } else {
      // CSV/Excel have standard column mappings
      const displayMap = this.columnLabelsValue

      Object.entries(displayMap).forEach(([key, label]) => {
        if (mappings[key]) {
          mappingHtml += `<div class="flex justify-between"><span>${label}:</span><span class="font-medium">${mappings[key]}</span></div>`
        }
      })
    }

    this.detectedMappingsTarget.innerHTML = mappingHtml || `<p>${this.noMappingsTextValue}</p>`
    this.analysisResultsTarget.classList.remove("hidden")
  }

  showAnalysisError(message) {
    this.errorMessageTarget.textContent = message
    this.analysisErrorTarget.classList.remove("hidden")
  }

  applyDetectedMappings(event) {
    if (!this.detectedData || !this.detectedData.mappings) return

    const mappings = this.detectedData.mappings
    const formatType = this.formatTypeTarget.value

    if (formatType !== "pdf") {
      // Apply CSV/Excel column mappings
      Object.entries(mappings).forEach(([key, value]) => {
        const input = document.querySelector(`[name*="[column_mapping][${key}]"]`)
        if (input) {
          input.value = value
        }
      })
    }

    // Provide visual feedback
    const button = event.target
    const originalText = button.textContent
    button.textContent = this.appliedTextValue
    button.classList.add("text-green-600")

    setTimeout(() => {
      button.textContent = originalText
      button.classList.remove("text-green-600")
    }, 2000)
  }
}
